import { createSelector } from '@reduxjs/toolkit';
import {
  companyIsSoleTrader,
  companyIsStockCompany,
  marketHasSepaDirectDebitFeature,
} from '~/constants/markets';
import { isBeneficialOwner, personsAreTheSame } from '~/helpers/shared.helper';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { SignerType } from '~/enums';
import { stepConfigPerMarket } from '~/pages/LoanOffer/components/stepConfigs';
import { StepConfigItem } from '~/Interfaces';
import { RootState } from '..';

const selectApplicantHasAnsweredBeneficialOwnersQuestion = createSelector(
  [(state: RootState) => state.loanOffer.form.companyHasOtherBeneficialOwners],
  (companyHasOtherBeneficialOwners) =>
    typeof companyHasOtherBeneficialOwners === 'boolean'
);

const selectApplicantHasAnswered100PercentOwnershipQuestion = createSelector(
  [(state: RootState) => state.loanOffer.form.applicantOwns100PercentOfShares],
  (applicantOwns100PercentOfShares) =>
    typeof applicantOwns100PercentOfShares === 'boolean'
);

const selectCompanyIsStockCompany = createSelector(
  [
    (state: RootState) => state.loanOffer.overview.companyType,
    (state: RootState) => state.intl.market,
  ],
  (companyType, market) => companyIsStockCompany(market, companyType)
);

const selectApplicantIsABeneficialOwner = createSelector(
  [(state: RootState) => state.loanOffer.form.applicant.percentage],
  (applicantOwnerPercentage) => isBeneficialOwner(applicantOwnerPercentage)
);

const selectApplicantIsSoloBeneficialOwner = createSelector(
  [
    (state: RootState) => state.loanOffer.form.applicant.percentage,
    (state: RootState) => state.loanOffer.form.companyHasOtherBeneficialOwners,
  ],
  (applicantOwnerPercentage, companyHasOtherBeneficialOwners) =>
    isBeneficialOwner(applicantOwnerPercentage) &&
    (companyHasOtherBeneficialOwners === false ||
      Number(applicantOwnerPercentage) >= 75)
);

const selectOwnersDataIsFetched = createSelector(
  [(state: RootState) => state.loanOffer.fetchedBeneficialOwners],
  (fetchedBeneficialOwners) => !!fetchedBeneficialOwners.length
);

const selectTotalAmountOfShares = createSelector(
  [
    (state: RootState) => state.loanOffer.form.applicant.percentage,
    (state: RootState) => state.loanOffer.form.owners,
    selectOwnersDataIsFetched,
  ],
  (applicantOwnerPercentage, owners, ownersDataIsFetched) => {
    if (ownersDataIsFetched) {
      return owners
        .filter((owner) => !owner.isAlternativeBeneficialOwner)
        .reduce((acc, owner) => acc + Number(owner.percentage), 0);
    }

    return owners
      .filter((owner) => !owner.isAlternativeBeneficialOwner)
      .reduce(
        (acc, owner) => acc + Number(owner.percentage),
        Number(applicantOwnerPercentage)
      );
  }
);

const selectFetchedOwnersHaveBeenChanged = createSelector(
  [
    (state: RootState) => state.loanOffer.form.owners,
    (state: RootState) => state.loanOffer.fetchedBeneficialOwners,
  ],
  (owners, fetchedBeneficialOwners) => {
    if (!fetchedBeneficialOwners.length) {
      return false;
    }

    const hasChanged =
      fetchedBeneficialOwners.length !== owners.length ||
      fetchedBeneficialOwners.some((fetchedOwner) => {
        const owner = owners.find((o) => o.id === fetchedOwner.id);

        if (
          !owner ||
          owner.fullName !== fetchedOwner.fullName ||
          owner.personalNumber !== fetchedOwner.personalNumber ||
          owner.percentage !== fetchedOwner.percentage ||
          owner.countryOfResidence !== fetchedOwner.countryOfResidence ||
          (fetchedOwner.dateOfBirth &&
            moment(owner.dateOfBirth).format('YYYY-MM-DD') !==
              moment(fetchedOwner.dateOfBirth).format('YYYY-MM-DD')) ||
          owner.yearOfBirth !== fetchedOwner.yearOfBirth
        ) {
          return true;
        }

        return false;
      });

    return hasChanged;
  }
);

export const selectLoanAmount = createSelector(
  [
    (state: RootState) => state.loanOffer.form.payoutAmount,
    (state: RootState) => state.loanOffer.overview.currentLoanAmountLeft,
  ],
  (amount, currentLoanAmountLeft) => amount + (currentLoanAmountLeft ?? 0)
);

const selectAlternativeOwnerManuallyAdded = createSelector(
  [(state: RootState) => state.loanOffer.form.owners],
  (owners) => {
    return owners.some((owner) => owner.isAlternativeBeneficialOwner);
  }
);

export const selectHasSepaDirectDebitFeature = createSelector(
  [(state: RootState) => state.intl.market],
  (market) => Boolean(marketHasSepaDirectDebitFeature[market])
);

export const selectStepConfig = createSelector(
  [
    (state: RootState) => state.loanOffer.form.owners,
    (state: RootState) => state.loanOffer.form.applicant,
    (state: RootState) => state.loanOffer.overview.companyType,
    (state: RootState) => state.loanOffer.overview.loanPurpose,
    (state: RootState) => state.loanOffer.overview.signers,
    (state: RootState) => state.intl.market,
  ],
  (owners, applicant, companyType, loanPurpose, signers, market) => {
    const stepConfig = stepConfigPerMarket[market].filter(
      (step: StepConfigItem) => {
        let hideStep = false;

        switch (step.name) {
          case 'ownership_step': // hide ownership step if company is sole trader
            hideStep = companyIsSoleTrader(market, companyType);
            break;
          case 'loan_purpose_step': // hide loan purpose step if we already have a loan purpose
            hideStep = !!loanPurpose;
            break;
          case 'additional_signers_and_guarantors_step': {
            // hide step if there are no additional signers and the applicant is an owner.
            // If the applicant is an owner, it means that the AML/KYC data will be added to the applicant in the owners step.
            const hasAdditionalSigners = signers.some(
              (signer) =>
                signer.type === SignerType.Signer ||
                signer.type === SignerType.Guarantor
            );
            const applicantExistsInListOfOwners = !!owners.find((owner) =>
              personsAreTheSame(owner, applicant)
            );
            hideStep = !hasAdditionalSigners && applicantExistsInListOfOwners;
            break;
          }
          default:
            break;
        }

        return !hideStep;
      }
    );
    return stepConfig;
  }
);

export const useLoanOfferSelectors = () => ({
  applicantHasAnsweredBeneficialOwnersQuestion: useSelector(
    selectApplicantHasAnsweredBeneficialOwnersQuestion
  ),
  applicantHasAnswered100PercentOwnershipQuestion: useSelector(
    selectApplicantHasAnswered100PercentOwnershipQuestion
  ),
  companyIsStockCompany: useSelector(selectCompanyIsStockCompany),
  applicantIsABeneficialOwner: useSelector(selectApplicantIsABeneficialOwner),
  applicantIsSoloBeneficialOwner: useSelector(
    selectApplicantIsSoloBeneficialOwner
  ),
  totalAmountOfShares: useSelector(selectTotalAmountOfShares),
  ownersDataIsFetched: useSelector(selectOwnersDataIsFetched),
  fetchedOwnersHaveBeenChanged: useSelector(selectFetchedOwnersHaveBeenChanged),
  loanAmount: useSelector(selectLoanAmount),
  alternativeOwnerManuallyAdded: useSelector(
    selectAlternativeOwnerManuallyAdded
  ),
  stepConfig: useSelector(selectStepConfig),
  hasSepaDirectDebitFeature: useSelector(selectHasSepaDirectDebitFeature),
});
